import React, { useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  BUDDY_HOME_ATTRIBUTE,
  OrbBuddies,
  ORB_TIPS_BELOW,
  ORB_TIPS_SIDE,
  StepList,
  STEP_LIST_DENSITY_COMPACT,
  type BuddiesWho,
} from '@aibuild/ui';
import type { ToolExecution } from '@hooks/useChat';
import { useElementSize } from '@hooks/useElementSize';
import { BUDDY_HOME_PREVIEW, BUILDING_ORB_PX, BUILDING_ORB_SMALL_PX, BUILDING_SIDE_TIPS_MIN_PX } from './builderWorkspace.constants';
import { stepLabel, stepListOf, type BuildStatus } from './buildSteps';
import { BuildingRoot, Description, OrbHome, ProgressTrack, Steps, Title } from './BuildingScreen.styled';

interface BuildingScreenProps {
  readonly status: BuildStatus;
  readonly steps: readonly ToolExecution[];
  readonly who: BuddiesWho;
}

const BuildingScreen: React.FC<BuildingScreenProps> = ({ status, steps, who }) => {
  const intl = useIntl();
  const rootRef = useRef<HTMLDivElement>(null);
  const { width } = useElementSize(rootRef);
  const roomy = width >= BUILDING_SIDE_TIPS_MIN_PX;
  const now = status.current
    ? stepLabel(intl, status.current)
    : intl.formatMessage({ id: 'apps.builder.building.reading', defaultMessage: 'Reading your brief…' });
  const tips = useMemo(() => [now], [now]);
  const list = stepListOf(intl, steps);

  return (
    <BuildingRoot ref={rootRef} data-testid="app-builder__building">
      <OrbHome {...{ [BUDDY_HOME_ATTRIBUTE]: BUDDY_HOME_PREVIEW }}>
        <OrbBuddies
          size={roomy ? BUILDING_ORB_PX : BUILDING_ORB_SMALL_PX}
          who={who}
          busy
          tips={tips}
          tipPlacement={roomy ? ORB_TIPS_SIDE : ORB_TIPS_BELOW}
          dataTestId="app-builder__building-buddies"
        />
      </OrbHome>
      <Title>{intl.formatMessage({ id: 'apps.builder.building.title', defaultMessage: 'Building your app' })}</Title>
      <Description>
        {intl.formatMessage({
          id: 'apps.builder.building.body',
          defaultMessage: 'Your viewer and controls appear here in a minute or two.',
        })}
      </Description>
      <ProgressTrack
        role="progressbar"
        aria-busy="true"
        aria-label={intl.formatMessage({ id: 'apps.builder.building.progress', defaultMessage: 'Building' })}
      />
      {list.length > 0 && (
        <Steps>
          <StepList steps={list} density={STEP_LIST_DENSITY_COMPACT} />
        </Steps>
      )}
    </BuildingRoot>
  );
};

export default BuildingScreen;
